/* eslint-disable @typescript-eslint/naming-convention */
export interface GoogleConfig {
  authority: string;
  client_id: string;
  redirect_uri: string;
  response_type: string;
  scope: string;
  post_logout_redirect_uri: string;
  extraQueryParams?: Record<string, string>;
}

// Mirrors entraConfig.ts: values are injected by rspack DefinePlugin from
// process.env.XEC_GOOGLE_* (.env.gcp).
//
// HISTORY, because this comment has been wrong twice and the wrong versions
// cost real debugging time:
//
//   1. First it said oidc-client-ts could drive Google directly, like Entra.
//      It cannot. Google's "Web application" client is CONFIDENTIAL: its token
//      endpoint requires `client_secret` even with PKCE, and a browser cannot
//      hold one. The redirect reached /callback with a valid code and the
//      exchange died there — `Failed to fetch`, entirely between the browser
//      and accounts.google.com.
//   2. Then it said GIS was the answer. GIS did solve the exchange, but it
//      issues one ID token and offers no refresh, so sessions ended after an
//      hour, and its button had to be a popup.
//
// What is true now: the flow is authorization-code + PKCE by full-page
// redirect, and the exchange happens in the BACKEND (app/_oauth.py), which
// holds the secret and keeps the refresh token. These values configure the
// browser's half — the authorize request — only.
//
// `response_type` and `redirect_uri` are live again for that reason; they were
// dead weight during the GIS period.
export const googleAuthConfig: GoogleConfig = {
  // The discovery document lives at <authority>/.well-known/openid-configuration.
  // Note the ISSUER in the resulting token may be either
  // "https://accounts.google.com" or the bare "accounts.google.com" — both are
  // legal and Google emits both. oidc-client-ts accepts what discovery returns,
  // and the BACKEND accepts both spellings (02i-identity's
  // google_oauth_issuer_urls lists them explicitly). Do not "fix" one of them.
  // @ts-ignore
  authority: process.env.XEC_GOOGLE_AUTHORITY || "https://accounts.google.com",
  // @ts-ignore
  client_id: process.env.XEC_GOOGLE_CLIENT_ID || "",
  // @ts-ignore
  redirect_uri: process.env.XEC_GOOGLE_REDIRECT_URI || "",
  // @ts-ignore
  response_type: process.env.XEC_GOOGLE_RESPONSE_TYPE || "code",
  // Only three non-sensitive scopes. The five delegated API scopes Entra uses
  // (policies.read, tokens.write, …) were deliberately dropped for Google:
  // route authorization is done by the gateway middleware against Firestore
  // RBAC, not by scope. Keeping them non-sensitive is also what makes an
  // Internal consent screen exempt from Google's app verification.
  // @ts-ignore
  scope: process.env.XEC_GOOGLE_SCOPE || "openid email profile",
  // @ts-ignore
  post_logout_redirect_uri: process.env.XEC_GOOGLE_POST_LOGOUT_REDIRECT_URI || "",

  // `hd` asks Google to show only accounts from this Workspace domain in the
  // picker. It is a UX hint and NOT a security control — a determined caller
  // can strip it from the URL. The real boundary is the backend asserting the
  // `hd` CLAIM on the verified token (GCP_OAUTH_HOSTED_DOMAIN); this just stops
  // an employee's personal gmail account from being offered and then rejected.
  extraQueryParams: {
    // @ts-ignore
    hd: process.env.XEC_GOOGLE_HOSTED_DOMAIN || "",
  },
};

// Google has no RP-initiated logout that returns to the app.
//
// Hitting https://accounts.google.com/Logout signs the user out of EVERY Google
// session in that browser — Gmail, Calendar, everything — because leaving an
// internal admin tool is not a reason to end someone's whole Google session.
// Entra's /oauth2/v2.0/logout is scoped to the app; Google's is not, and there
// is no scoped equivalent.
//
// So local sign-out only: react-oidc's removeUser() has already cleared the
// tokens (see signOutClean in idp.ts) and we simply return to the app, which
// then renders the login page. The user stays signed in to Google, exactly as
// they would after closing any other Google-authenticated site.
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const googleSignOutRedirect = () => {
  const { post_logout_redirect_uri } = googleAuthConfig;
  window.location.href = post_logout_redirect_uri || window.location.origin;
};
